import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  GRANT_KINDS_WON,
  IDENTITIES,
  VISIT_REASONS,
  onboardingSchema,
  type GrantKindWonKey,
  type IdentityKey,
  type VisitReasonKey,
} from '@zoxo/shared';

import { PILL_TAB, PILL_TAB_OFF, PILL_TAB_ON } from '@/components/ui/pill-tab';
import { Stepper } from '@/components/ui/stepper';
import { cn } from '@/lib/utils';
import { useOnboarding } from './use-onboarding';

/**
 * 정체성 정하기 — **네 걸음.** 가입은 병아리로 바로 되고, 이건 언제든 하는 프로필 행동이다.
 *
 *   1 당신은 누구십니까 (🦁 사자 … 🐣 병아리)
 *   2 그래서 여기는 왜 오셨나요
 *   3 지금까지 몇 번이나 꽁돈을 따먹어 보셨나요
 *   4 (1번 이상이면) 어떤 걸 따먹어 봤나요 — 0번이면 건너뛴다
 *
 * 답은 마지막에 한 번에 보낸다 (POST /users/me/onboarding). 첫 온보딩이면 서버가 이름을
 * 정체성으로 짓는다 — 「🦅독수리4821」. 로그인한 사용자가 바꾸는 행동(투표·댓글·글)을 처음
 * 시도할 때 그 자리에 뜬다.
 */
const NEXT = cn(PILL_TAB, 'bg-primary text-primary-foreground hover:bg-primary-hover disabled:opacity-50');
const PREV = cn(PILL_TAB, PILL_TAB_OFF, 'border border-gray-200');

type Step = 1 | 2 | 3 | 4;
const STEP_LABEL: Record<Step, string> = { 1: '정체성', 2: '방문 이유', 3: '횟수', 4: '종류' };
const WINS_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: '0회' }, { value: 1, label: '1회' }, { value: 2, label: '2회' },
  { value: 3, label: '3회' }, { value: 4, label: '4회' }, { value: 5, label: '5회 이상' },
];

export function OnboardingForm({ onDone }: { onDone?: () => void } = {}) {
  const onboarding = useOnboarding();
  const [step, setStep] = useState<Step>(1);
  const [identity, setIdentity] = useState<IdentityKey | null>(null);
  const [visitReason, setVisitReason] = useState<VisitReasonKey | null>(null);
  const [grantWins, setGrantWins] = useState(0);
  const [grantKinds, setGrantKinds] = useState<GrantKindWonKey[]>([]);

  // 0번이면 4단계가 없다 — 표시용 걸음 수도 그만큼 준다.
  const steps: Step[] = grantWins > 0 ? [1, 2, 3, 4] : [1, 2, 3];
  const index = steps.indexOf(step);
  const canNext =
    step === 1 ? identity !== null
    : step === 2 ? visitReason !== null
    : step === 3 ? true
    : grantKinds.length > 0;
  const prev = () => setStep(steps[Math.max(index - 1, 0)]!);
  // 하나 고르는 걸음(1·2·3)은 고르는 순간 다음 걸음으로 — 「다음」 버튼이 없다.
  // 3에서 0회를 고르면 4가 없으니 그 자리에서 「완료」를 누른다.
  const pickIdentity = (key: IdentityKey) => { setIdentity(key); setStep(2); };
  const pickReason = (key: VisitReasonKey) => { setVisitReason(key); setStep(3); };
  const pickWins = (value: number) => { setGrantWins(value); if (value > 0) setStep(4); };

  const submit = () => {
    const parsed = onboardingSchema.safeParse({
      identity, visitReason, grantWins, grantKinds: grantWins > 0 ? grantKinds : [],
    });
    if (!parsed.success) return;
    onboarding.mutate(parsed.data, { onSuccess: () => onDone?.() });
  };

  return (
    <section>
      <Stepper steps={steps.map((st) => STEP_LABEL[st])} current={index} className="mb-6" />

      <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={step}
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -24 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
      {step === 1 && (
        <div className="mt-2">
          <h2 className="text-lg font-bold text-text">당신은 누구십니까?</h2>
          <p className="mt-1 text-sm text-text-muted">나를 설명하는 가장 적절한 identity 를 선택하세요.</p>
          <div role="radiogroup" aria-label="정체성" className="mt-4 grid gap-2">
            {IDENTITIES.map((it) => {
              const on = identity === it.key;
              return (
                <button
                  key={it.key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => pickIdentity(it.key)}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
                    on ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white hover:bg-gray-50',
                  )}
                >
                  <span className="text-2xl" aria-hidden>{it.emoji}</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold">{it.label}</span>
                    <span className={cn('block text-xs', on ? 'text-gray-300' : 'text-text-muted')}>{it.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-2">
          <h2 className="text-lg font-bold text-text">그래서 여기는 왜 오셨나요?</h2>
          <div role="radiogroup" aria-label="방문 이유" className="mt-4 grid gap-2">
            {VISIT_REASONS.map((r) => {
              const on = visitReason === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => pickReason(r.key)}
                  className={cn(
                    'rounded-xl border px-4 py-3 text-left text-sm transition-colors',
                    on ? 'border-gray-900 bg-gray-900 font-medium text-white' : 'border-gray-200 bg-white hover:bg-gray-50',
                  )}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="mt-2">
          <h2 className="text-lg font-bold text-text">지금까지 내가 참여해서 몇 번이나 꽁돈을 따먹어 보셨나요?</h2>
          <p className="mt-1 text-sm text-text-muted">
            발표 또는 질의응답 세션에 내가 참여한 경우만 카운팅하자.
          </p>
          {/* 라디오 — 0회 · 1 · 2 · 3 · 4 · 5회 이상. 5 는 「5회 이상」이다. */}
          <div role="radiogroup" aria-label="따먹은 횟수" className="mt-4 flex flex-wrap gap-1.5">
            {WINS_OPTIONS.map((o) => {
              const on = grantWins === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => pickWins(o.value)}
                  className={cn(PILL_TAB, on ? PILL_TAB_ON : cn(PILL_TAB_OFF, 'border border-gray-200'))}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="mt-2">
          <h2 className="text-lg font-bold text-text">어떤 걸 따먹어 봤었나요?</h2>
          <p className="mt-1 text-sm text-text-muted">여러 개 골라도 돼요.</p>
          <div role="group" aria-label="따먹은 종류" className="mt-4 flex flex-wrap gap-1.5">
            {GRANT_KINDS_WON.map((k) => {
              const on = grantKinds.includes(k.key);
              return (
                <button
                  key={k.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setGrantKinds((cur) => (on ? cur.filter((x) => x !== k.key) : [...cur, k.key]))}
                  className={cn(PILL_TAB, on ? PILL_TAB_ON : cn(PILL_TAB_OFF, 'border border-gray-200'))}
                >
                  {on ? '☑ ' : '☐ '}{k.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      </motion.div>
      </AnimatePresence>

      {onboarding.isError && (
        <p className="mt-4 text-sm text-danger" role="alert">
          저장에 실패했어요. 잠시 후 다시 시도해 주세요.
        </p>
      )}

      <div className="mt-6 flex items-center justify-between">
        {index > 0 ? (
          <button type="button" className={PREV} onClick={prev}>이전</button>
        ) : <span />}
        {index === steps.length - 1 ? (
          <button type="button" className={NEXT} disabled={!canNext || onboarding.isPending} onClick={submit}>
            {onboarding.isPending ? '저장 중…' : '완료'}
          </button>
        ) : <span />}
      </div>
    </section>
  );
}
